import React, { useState, useEffect, useRef } from 'react';

// Configuration - Parsed from environment variables at build time
// Use REACT_APP_* prefix for Create React App to inject these at build time
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://ny8qwv90wg.execute-api.us-east-1.amazonaws.com/prod';
const WEBSOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL || 'wss://y1qc77sbc0.execute-api.us-east-1.amazonaws.com/prod';

// Cognito Configuration - Parsed from environment variables at build time
const COGNITO_CONFIG = {
  region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
  userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'us-east-1_mqeL6YtPe',
  userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '7733fb58l664h3n8vl7fl99lre',
};

// SSO Configuration
const SSO_CONFIG = {
  enabled: process.env.REACT_APP_SSO_ENABLED === 'true',
  samlEnabled: process.env.REACT_APP_SAML_ENABLED === 'true',
  oidcEnabled: process.env.REACT_APP_OIDC_ENABLED === 'true',
  cognitoDomain: process.env.REACT_APP_COGNITO_DOMAIN || '',
  samlProviderName: process.env.REACT_APP_SAML_PROVIDER_NAME || 'NBCUniversal',
  oidcProviderName: process.env.REACT_APP_OIDC_PROVIDER_NAME || 'AzureADB2C',
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null); // ID token for API Gateway
  const [accessToken, setAccessToken] = useState(null); // Access token for WebSocket
  const [refreshToken, setRefreshToken] = useState(null); // Refresh token for renewing access
  const [tokenExpiry, setTokenExpiry] = useState(null); // Token expiration timestamp
  const [wsConnected, setWsConnected] = useState(false);
  const [currentAlert, setCurrentAlert] = useState(null);
  const [users, setUsers] = useState(null); // For superadmin
  const [inboxMessages, setInboxMessages] = useState([]); // For inbox - shown on dashboard
  const [inboxLoading, setInboxLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [updateProgress, setUpdateProgress] = useState(null);
  const [updateDownloaded, setUpdateDownloaded] = useState(false);
  
  const websocketRef = useRef(null);
  const alertSoundIntervalRef = useRef(null);

  // Detect if running in desktop app
  useEffect(() => {
    if (window.electronAPI) {
      setIsDesktop(true);
      setAudioEnabled(true); // Desktop audio always works
      console.log('🖥️ Running in desktop app - reliable audio enabled');
      
      // Set up update listeners
      window.electronAPI.onUpdateAvailable((event, info) => {
        console.log('📥 Update available:', info);
        setUpdateInfo(info);
      });
      
      window.electronAPI.onUpdateProgress((event, progress) => {
        console.log('📊 Update progress:', progress);
        setUpdateProgress(progress);
      });
      
      window.electronAPI.onUpdateDownloaded((event, info) => {
        console.log('✅ Update downloaded:', info);
        setUpdateDownloaded(true);
        setUpdateProgress(null);
      });
      
      // Cleanup listeners on unmount
      return () => {
        if (window.electronAPI && window.electronAPI.removeUpdateListeners) {
          window.electronAPI.removeUpdateListeners();
        }
      };
    }
  }, []);

  // Simple audio enable on first interaction (web only)
  useEffect(() => {
    if (isDesktop) return; // Skip for desktop
    
    const enableAudio = () => {
      if (!audioEnabled) {
        setAudioEnabled(true);
        console.log('🔊 Audio enabled after user interaction');
      }
    };
    
    // Listen for any user interaction to enable audio
    document.addEventListener('click', enableAudio, { once: true });
    document.addEventListener('keydown', enableAudio, { once: true });
    document.addEventListener('touchstart', enableAudio, { once: true });
    
    return () => {
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
    };
  }, [audioEnabled, isDesktop]);
  
  // Initialize - no session persistence for now (user needs to login each time)
  useEffect(() => {
    // Could add session persistence with localStorage here if needed
    console.log('App initialized');
  }, []);
  
  // Fetch unacknowledged alerts on login
  const fetchInboxOnLogin = async () => {
    await fetchInboxMessages(true);
  };

  // Fetch inbox on login and periodically
  const fetchInboxMessages = async (showAlert = false) => {
    setInboxLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/inbox`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = await response.json();
      
      if (response.ok && data.messages) {
        setInboxMessages(data.messages || []);
        if (showAlert && data.messages.length > 0) {
          // Show first unacknowledged alert
          const latestAlert = data.messages[0];
          handleNewAlert(latestAlert);
        }
      }
    } catch (error) {
      console.error('Error fetching inbox:', error);
    } finally {
      setInboxLoading(false);
    }
  };

  // Auto-refresh inbox every 10 seconds for real-time updates
  useEffect(() => {
    if (!user || !token) return;
    
    const interval = setInterval(() => {
      fetchInboxMessages(false);
    }, 10000);
    
    return () => clearInterval(interval);
  }, [user, token]);

  // Auto-refresh live connections every 3 seconds for SuperAdmins (real-time online status)
  useEffect(() => {
    if (!user || !token || user.role !== 'superadmin') return;
    
    const interval = setInterval(() => {
      fetchLiveConnections();
    }, 3000);
    
    return () => clearInterval(interval);
  }, [user, token, users]);

  // Auto-refresh access token before expiry
  useEffect(() => {
    if (!user || !tokenExpiry || !refreshToken) return;
    
    const checkTokenExpiry = () => {
      const now = Date.now();
      const timeUntilExpiry = tokenExpiry - now;
      
      // If token expires in less than 5 minutes, refresh it
      if (timeUntilExpiry < 5 * 60 * 1000 && timeUntilExpiry > 0) {
        console.log('🔄 Token expiring soon, refreshing...');
        refreshAccessToken();
      }
    };
    
    // Check every minute
    const interval = setInterval(checkTokenExpiry, 60 * 1000);
    
    // Check immediately
    checkTokenExpiry();
    
    return () => clearInterval(interval);
  }, [user, tokenExpiry, refreshToken]);

  // WebSocket connection
  useEffect(() => {
    if (user && accessToken && !websocketRef.current) {
      connectWebSocket();
    }
    
    return () => {
      if (websocketRef.current) {
        websocketRef.current.close();
      }
    };
  }, [user, accessToken]);

  const connectWebSocket = () => {
    try {
      const ws = new WebSocket(WEBSOCKET_URL);
      websocketRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
        // Authenticate the connection with access token
        ws.send(JSON.stringify({
          type: 'authenticate',
          token: accessToken // Use access token for WebSocket auth
        }));
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        console.log('🔔 WebSocket message received:', data);
        console.log('🔔 Message type:', data.type);
        console.log('🔔 Full message data:', JSON.stringify(data, null, 2));

        switch (data.type) {
          case 'authenticated':
            console.log('✅ WebSocket authenticated successfully');
            break;
          case 'new_alert':
            console.log('🚨 NEW ALERT received via WebSocket:', data.alert);
            console.log('🚨 Alert data:', JSON.stringify(data.alert, null, 2));
            handleNewAlert(data.alert);
            break;
          case 'alert_acknowledged':
            console.log('✅ Alert acknowledged:', data);
            handleAlertAcknowledged(data);
            break;
          case 'auth_error':
            console.error('❌ ❌ ❌ WEBSOCKET AUTH ERROR - LOGGING OUT');
            console.error('Error message:', data.message);
            console.error('User role:', user?.role);
            console.error('User status:', user?.status);
            console.error('This usually means: user status is pending/rejected, token is invalid, or user not found');
            logout();
            break;
          default:
            console.log('❓ Unknown message type:', data.type, data);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setWsConnected(false);
        websocketRef.current = null;
        // Attempt to reconnect after 5 seconds
        if (user && accessToken) {
          setTimeout(connectWebSocket, 5000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  };

  const handleNewAlert = (alert) => {
    console.log('🚨 handleNewAlert called with:', alert);
    console.log('👤 Current user:', user);
    console.log('👤 Current user role:', user?.role);
    
    // Only show alert popup and sound for regular users (as per AI_CONTEXT.md design)
    if (user?.role === 'user') {
      // Validate if this alert is for the current user
      const isForThisUser = alert.targetType === user.stationType || 
                           (alert.targetType === 'specific' && alert.targetUserId === user.id);
      
      if (isForThisUser) {
        console.log('🚨 Showing alert popup and starting sound for user');
        setCurrentAlert(alert);
        startAlertSound();
      } else {
        console.log('⚠️ Alert received but not for this user - targetType:', alert.targetType, 'userStationType:', user.stationType);
      }
    } else {
      console.log('🚫 Alert received but no popup/sound for role:', user?.role, '(admins don\'t get popups)');
    }
  };

  const handleAlertAcknowledged = (data) => {
    // Silent acknowledgment - no popup for admins
    console.log(`✅ Alert acknowledged by ${data.acknowledgedBy?.username || 'unknown'}`);
    if (['admin', 'superadmin'].includes(user.role)) {
    }
  };

  const startAlertSound = async () => {
    // Only play sound for users with role 'user' (not admin/superadmin)
    if (user?.role !== 'user') {
      console.log('🚫 Sound blocked - user role is:', user?.role, '(only regular users get sound)');
      return;
    }
    
    // Desktop app: Use reliable system audio
    if (isDesktop && window.electronAPI) {
      console.log('🖥️ Using desktop audio - guaranteed to work!');
      try {
        await window.electronAPI.startAlertSound();
        console.log('🔊 Desktop alert sound started');
        return;
      } catch (error) {
        console.error('❌ Desktop audio failed:', error);
        // Fall back to web audio below
      }
    }
    
    // Web browser: Check if audio is enabled (user has interacted with page)
    if (!audioEnabled) {
      console.log('⚠️ Audio not enabled yet - user needs to click on page first');
      // Show notification instead
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('🚨 EMERGENCY ALERT', {
          body: 'Click anywhere on the page to enable sound alerts!',
          requireInteraction: true,
          tag: 'audio-enable-prompt'
        });
      }
      return;
    }
    
    // Create audio context for beeping sound
    if (alertSoundIntervalRef.current) {
      console.log('🔊 Sound already playing, not starting new interval');
      return; // Already playing
    }

    let audioContext = null;
    
    const playBeep = async () => {
      try {
        // Method 1: Try AudioContext
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        // Resume if suspended (for Safari) - MUST be async
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
          console.log('🔊 Audio context resumed');
        }
        
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = 800;
        oscillator.type = 'square';
        
        gainNode.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.5, audioContext.currentTime + 0.01);
        gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
        
        console.log('🔊 Alert beep played at', new Date().toLocaleTimeString());
        
        // Clean up
        setTimeout(() => {
          if (audioContext && audioContext.state !== 'closed') {
            audioContext.close();
          }
        }, 600);
        
        } catch (error) {
        console.error('❌ Error playing beep:', error);
        
        // Method 2: Try HTML5 Audio as fallback
        try {
          // Create a data URL for a beep sound
          const audioData = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhEjuU2/LBdSMFl8Fw';
          const audio = new Audio(audioData);
          audio.volume = 0.7;
          audio.play().then(() => {
            console.log('🔊 Fallback audio played');
          }).catch((audioError) => {
            console.error('❌ Fallback audio failed:', audioError);
          });
        } catch (audioFallbackError) {
          console.error('❌ Audio fallback failed:', audioFallbackError);
        }
        
        // Method 3: Browser notification with sound
        try {
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('🚨 EMERGENCY ALERT', {
              body: 'ALERT: Please acknowledge immediately! Browser audio blocked.',
              icon: '/favicon.ico',
              requireInteraction: true,
              tag: 'emergency-alert',
              silent: false // Try to make sound
            });
            console.log('📢 Notification sent as audio fallback');
            
            // Try to focus window to get user's attention
            if (window.focus) {
              window.focus();
            }
          }
        } catch (notifError) {
          console.error('❌ Notification fallback failed:', notifError);
        }
        
        // Method 4: Visual alert as last resort
        document.body.style.animation = 'blink 0.5s infinite';
        setTimeout(() => {
          document.body.style.animation = '';
        }, 5000);
      }
    };

    // Play first beep immediately
    console.log('🔊 Starting alert sound for user role:', user?.role);
    playBeep();
    
    // Then play every 2 seconds
    alertSoundIntervalRef.current = setInterval(async () => {
      console.log('🔊 Playing scheduled beep...');
      await playBeep();
    }, 2000);
    
    console.log('🔊 Alert sound interval started - will beep every 2 seconds');
  };

  const stopAlertSound = async () => {
    // Desktop app: Stop system audio
    if (isDesktop && window.electronAPI) {
      try {
        await window.electronAPI.stopAlertSound();
        console.log('🔇 Desktop alert sound stopped');
      } catch (error) {
        console.error('❌ Error stopping desktop audio:', error);
      }
    }
    
    // Web browser: Stop interval
    if (alertSoundIntervalRef.current) {
      clearInterval(alertSoundIntervalRef.current);
      alertSoundIntervalRef.current = null;
      console.log('🔇 Web alert sound stopped');
    }
  };

  const acknowledgeAlert = async () => {
    if (!currentAlert) return;

    try {
      const response = await fetch(`${API_BASE_URL}/alerts/${currentAlert.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        stopAlertSound();
        setCurrentAlert(null);
        console.log('✅ Alert acknowledged successfully');
      }
    } catch (error) {
      console.error('❌ Error acknowledging alert:', error);
    }
  };

  const refreshAccessToken = async () => {
    if (!refreshToken) {
      console.error('❌ No refresh token available');
      logout();
      return false;
    }

    try {
      console.log('🔄 Refreshing access token...');
      const response = await fetch(`${API_BASE_URL}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'refresh',
          refreshToken: refreshToken 
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.token) {
        setToken(data.token);
        setAccessToken(data.accessToken);
        if (data.refreshToken) {
          setRefreshToken(data.refreshToken);
        }
        setTokenExpiry(Date.now() + 55 * 60 * 1000); // Reset expiry to 55 minutes from now
        console.log('✅ Token refreshed successfully');
        return true;
      } else {
        console.error('❌ Token refresh failed:', data.error);
        logout();
        return false;
      }
    } catch (error) {
      console.error('❌ Token refresh error:', error);
      logout();
      return false;
    }
  };

  const login = async (email, password) => {
    setIsLoading(true);
    try {
      // Sign in through custom auth Lambda (which validates with Cognito)
      const response = await fetch(`${API_BASE_URL}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'login', email, password }),
      });

      const data = await response.json();
      
      if (response.ok) {
        // Store the ID token for API calls and access token for WebSocket
        setToken(data.token); // ID token
        setAccessToken(data.accessToken); // Access token
        setRefreshToken(data.refreshToken); // Refresh token
        setTokenExpiry(Date.now() + 55 * 60 * 1000); // Set expiry to 55 minutes from now (5 min buffer)
        setUser(data.user);
        
        // Request audio and notification permissions for alert system
        if (data.user.role === 'user') {
          requestPermissions();
        }
        
        return { success: true };
      } else {
        console.error('❌ Login failed:', data.error || 'Login failed');
        return { success: false, error: data.error || data.message };
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      return { success: false, error: 'Login failed - check your connection' };
    } finally {
      setIsLoading(false);
    }
  };

  // Handle SSO login redirect
  const handleSSOLogin = (providerName) => {
    if (!SSO_CONFIG.cognitoDomain) {
      console.error('SSO is not properly configured');
      return;
    }

    const redirectUri = window.location.origin; // Use root domain as callback
    const state = Math.random().toString(36).substring(7); // CSRF protection
    
    // Store state for validation on callback
    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_provider', providerName);
    
    // Build OAuth authorization URL
    const authUrl = `https://${SSO_CONFIG.cognitoDomain}.auth.${COGNITO_CONFIG.region}.amazoncognito.com/oauth2/authorize?` +
      `client_id=${COGNITO_CONFIG.userPoolClientId}&` +
      `response_type=code&` +
      `scope=openid+email+profile&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `identity_provider=${providerName}&` +
      `state=${state}`;
    
    console.log('🔐 Redirecting to SSO login:', providerName);
    window.location.href = authUrl;
  };
  
  // Check for OAuth callback on mount
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');
      
      // Not an OAuth callback
      if (!code && !error) return;
      
      // Handle OAuth error
      if (error) {
        console.error('❌ OAuth error:', error);
        const errorDescription = urlParams.get('error_description');
        alert(`SSO Login failed: ${errorDescription || error}`);
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      
      // Validate state (CSRF protection)
      const savedState = sessionStorage.getItem('oauth_state');
      if (state !== savedState) {
        console.error('❌ Invalid OAuth state');
        alert('SSO Login failed: Invalid state');
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      
      const provider = sessionStorage.getItem('oauth_provider');
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_provider');
      
      // Exchange code for tokens via API
      setIsLoading(true);
      try {
        const redirectUri = `${window.location.origin}/auth/callback`;
        const response = await fetch(`${API_BASE_URL}/auth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            action: 'sso_callback', 
            code, 
            redirectUri,
            provider 
          }),
        });

        const data = await response.json();
        
        if (response.ok && data.token) {
          console.log('✅ SSO login successful');
          
          // Store tokens
          setToken(data.token);
          setAccessToken(data.accessToken);
          setRefreshToken(data.refreshToken);
          setTokenExpiry(Date.now() + 55 * 60 * 1000);
          setUser(data.user);
          
          // Request permissions for users
          if (data.user.role === 'user') {
            requestPermissions();
          }
          
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          console.error('❌ SSO callback failed:', data.error);
          alert(`SSO Login failed: ${data.error || 'Unknown error'}`);
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (error) {
        console.error('❌ SSO callback error:', error);
        alert('SSO Login failed - check your connection');
        window.history.replaceState({}, document.title, window.location.pathname);
      } finally {
        setIsLoading(false);
      }
    };
    
    handleOAuthCallback();
  }, []);
  
  const requestPermissions = async () => {
    try {
      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        console.log('🔔 Notification permission:', permission);
      }
      
      // Initialize audio context with user gesture (helps with autoplay policy)
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        if (audioContext.state === 'suspended') {
          // This user interaction allows us to resume audio later
          document.addEventListener('click', async () => {
            if (audioContext.state === 'suspended') {
              await audioContext.resume();
              console.log('🔊 Audio context initialized after user interaction');
            }
          }, { once: true });
        }
        console.log('🔊 Audio context ready for alerts');
      } catch (audioError) {
        console.error('❌ Audio context initialization failed:', audioError);
      }
    } catch (error) {
      console.error('❌ Permission request failed:', error);
    }
  };

  const register = async (username, email, password, stationType) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'register', 
          username, 
          email, 
          password, 
          stationType 
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        // New workflow: users are pending approval, so no immediate login
        console.log('✅ Registration successful - pending approval');
        // Error/success will be shown in LoginPage component
        return { success: true, message: data.message };
      } else {
        console.error('❌ Registration failed:', data.error || 'Registration failed');
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('❌ Registration error:', error);
      return { success: false, error: 'Registration failed - check your connection' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setAccessToken(null);
    setRefreshToken(null);
    setTokenExpiry(null);
    setCurrentAlert(null);
    stopAlertSound();
    
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }
  };

  const createAlert = async (message, targetType, targetUserId = null) => {
    console.log('📢 CREATING ALERT:');
    console.log('- Message:', message);
    console.log('- Target Type:', targetType);
    console.log('- Target User ID:', targetUserId);
    console.log('- Current User:', user);
    
    try {
      const alertData = { message, targetType, targetUserId };
      console.log('📢 Sending alert data:', JSON.stringify(alertData, null, 2));
      
      const response = await fetch(`${API_BASE_URL}/alerts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(alertData),
      });

      console.log('📢 Alert API response status:', response.status);
      const data = await response.json();
      console.log('📢 Alert API response data:', JSON.stringify(data, null, 2));
      
      if (response.ok) {
        console.log('✅ Alert sent successfully!');
        // Success - no popup needed
      } else {
        console.error('❌ Alert sending failed:', data);
      }
    } catch (error) {
      console.error('❌ Error sending alert:', error);
    }
  };

  const fetchUsers = async () => {
    if (user.role !== 'superadmin' || !token) {
      console.log('⚠️ fetchUsers skipped - role:', user.role, 'token exists:', !!token);
      return;
    }
    
    console.log('📋 Fetching users with token:', token.substring(0, 20) + '...');
    
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = await response.json();
      
      if (response.ok) {
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Fetch live connection data to show real-time online status
  const fetchLiveConnections = async () => {
    if (user.role !== 'superadmin' || !users) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/connections`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = await response.json();
      
      if (response.ok && data.connections) {
        // Map active connections by userId for easy lookup
        const activeConnections = new Map();
        data.connections.forEach(conn => {
          if (conn.userId) {
            activeConnections.set(conn.userId, conn);
          }
        });
        
        // Update users to show current online status based on active connections
        const updatedUsers = {
          ...users,
          superadmins: users.superadmins.map(u => ({...u, isConnected: activeConnections.has(u.id)})),
          admins: users.admins.map(u => ({...u, isConnected: activeConnections.has(u.id)})),
          nbcu_users: users.nbcu_users.map(u => ({...u, isConnected: activeConnections.has(u.id)})),
          telemundo_users: users.telemundo_users.map(u => ({...u, isConnected: activeConnections.has(u.id)})),
          pending_users: users.pending_users.map(u => ({...u, isConnected: activeConnections.has(u.id)})),
          rejected_users: users.rejected_users.map(u => ({...u, isConnected: activeConnections.has(u.id)})),
        };
        setUsers(updatedUsers);
        console.log(`🟢 Updated online status for ${activeConnections.size} connected users`);
      }
    } catch (error) {
      console.error('Error fetching live connections:', error);
    }
  };

  const updateUserRole = async (userId, newRole, newStationType) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newRole, newStationType }),
      });

      if (response.ok) {
        console.log('✅ User updated successfully');
        fetchUsers(); // Refresh the list
      } else {
        const data = await response.json();
        console.error('❌ Failed to update user:', data.error);
      }
    } catch (error) {
      console.error('❌ Error updating user:', error);
    }
  };

  const approveUser = async (userId) => {
    // Direct approval - no confirmation dialog
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        console.log('✅ User approved successfully');
        fetchUsers(); // Refresh the list
      } else {
        const data = await response.json();
        console.error('❌ Failed to approve user:', data.error);
      }
    } catch (error) {
      console.error('❌ Error approving user:', error);
    }
  };

  const rejectUser = async (userId) => {
    // Direct rejection - no confirmation dialog
    try{
      const response = await fetch(`${API_BASE_URL}/users/${userId}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        console.log('✅ User rejected successfully');
        fetchUsers(); // Refresh the list
      } else {
        const data = await response.json();
        console.error('❌ Failed to reject user:', data.error);
      }
    } catch (error) {
      console.error('❌ Error rejecting user:', error);
    }
  };

  const deleteUser = async (userId) => {
    // Direct deletion - no confirmation dialog
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        console.log('✅ User deleted successfully');
        fetchUsers(); // Refresh the list
      } else {
        const data = await response.json();
        console.error('❌ Failed to delete user:', data.error);
      }
    } catch (error) {
      console.error('❌ Error deleting user:', error);
    }
  };

  const deleteMessage = async (messageId) => {
    // Direct deletion - no confirmation dialog
    try {
      const response = await fetch(`${API_BASE_URL}/alerts/${messageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        console.log('✅ Message deleted successfully');
        await fetchInboxMessages(false); // Refresh inbox
      } else {
        const data = await response.json();
        console.error('❌ Failed to delete message:', data.error);
      }
    } catch (error) {
      console.error('❌ Error deleting message:', error);
    }
  };

  // Render components based on authentication state
  if (!user) {
    return <LoginPage onLogin={login} onRegister={register} isLoading={isLoading} handleSSOLogin={handleSSOLogin} />;
  }

  return (
    <div className="container">
      <ConnectionStatus connected={wsConnected} />
      
      {/* Header */}
      <div className="card" style={{
        background: '#ffffff',
        borderBottom: '1px solid #e8eaed',
        color: '#1f2937',
        borderRadius: '0',
        padding: '20px 24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: '0 0 4px 0', fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>SOS Alert System</h1>
            <p style={{ margin: '0', color: '#6b7280', fontSize: '13px' }}>{user.email} • <span style={{ fontWeight: '500', color: '#1f2937' }}>{user.role === 'superadmin' ? 'Super Admin' : user.role.charAt(0).toUpperCase() + user.role.slice(1)}</span></p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '12px', fontWeight: '500', color: wsConnected ? '#059669' : '#ef4444', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: wsConnected ? '#059669' : '#ef4444', display: 'inline-block' }}></span>
                {wsConnected ? 'Connected' : 'Offline'}
              </div>
              {user.stationType && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{user.stationType} Station</div>}
            </div>
            <button className="button" onClick={logout} style={{ 
              background: '#f3f4f6', 
              border: '1px solid #d1d5db',
              color: '#1f2937',
              padding: '8px 16px',
              cursor: 'pointer',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '500',
              transition: 'all 0.2s',
              hover: { background: '#e5e7eb' }
            }}>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Desktop App Update Notifications */}
      {isDesktop && (updateInfo || updateProgress || updateDownloaded) && (
        <UpdateNotification 
          updateInfo={updateInfo}
          updateProgress={updateProgress}
          updateDownloaded={updateDownloaded}
          onDismiss={() => {
            setUpdateInfo(null);
            setUpdateProgress(null);
            setUpdateDownloaded(false);
          }}
        />
      )}
      
      {currentAlert && (
        <AlertPopup 
          alert={currentAlert} 
          onAcknowledge={acknowledgeAlert}
          userRole={user.role}
        />
      )}

      {/* Main Content */}
      {user.role === 'user' && <UserDashboard messages={inboxMessages} loading={inboxLoading} />}
      
      {['admin', 'superadmin'].includes(user.role) && (
        <AdminDashboard 
          onCreateAlert={createAlert}
          userRole={user.role}
          users={users}
          onFetchUsers={fetchUsers}
          onUpdateUser={updateUserRole}
          onApproveUser={approveUser}
          onRejectUser={rejectUser}
          onDeleteUser={deleteUser}
          token={token}
          inboxMessages={inboxMessages}
          inboxLoading={inboxLoading}
          onDeleteMessage={deleteMessage}
          onRefreshInbox={() => fetchInboxMessages(false)}
        />
      )}
    </div>
  );
}

// Connection status indicator
function ConnectionStatus({ connected }) {
  return (
    <div className={`connection-status ${connected ? 'connected' : 'disconnected'}`}>
      {connected ? '🟢 Connected' : '🔴 Disconnected'}
    </div>
  );
}

// Update notification for desktop app
function UpdateNotification({ updateInfo, updateProgress, updateDownloaded, onDismiss }) {
  const getNotificationStyle = () => {
    if (updateDownloaded) {
      return {
        backgroundColor: '#d4edda',
        borderColor: '#c3e6cb',
        color: '#155724'
      };
    } else if (updateProgress) {
      return {
        backgroundColor: '#fff3cd',
        borderColor: '#ffeaa7',
        color: '#856404'
      };
    } else {
      return {
        backgroundColor: '#d1ecf1',
        borderColor: '#bee5eb',
        color: '#0c5460'
      };
    }
  };

  const getNotificationContent = () => {
    if (updateDownloaded) {
      return {
        icon: '✅',
        title: 'Update Ready!',
        message: 'App will restart in 5 seconds to apply the update.'
      };
    } else if (updateProgress) {
      return {
        icon: '📥',
        title: 'Downloading Update...',
        message: `${Math.round(updateProgress.percent)}% complete (${Math.round(updateProgress.bytesPerSecond / 1024)} KB/s)`
      };
    } else if (updateInfo) {
      return {
        icon: '🆕',
        title: 'Update Available',
        message: `Version ${updateInfo.version} is downloading in the background.`
      };
    }
  };

  const content = getNotificationContent();
  if (!content) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      zIndex: 10000,
      padding: '15px 20px',
      border: '1px solid',
      borderRadius: '8px',
      minWidth: '300px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      ...getNotificationStyle()
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: '20px', marginRight: '10px' }}>{content.icon}</span>
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{content.title}</div>
            <div style={{ fontSize: '13px', opacity: 0.9 }}>{content.message}</div>
          </div>
        </div>
        {!updateDownloaded && (
          <button 
            onClick={onDismiss}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              opacity: 0.7,
              marginLeft: '10px'
            }}
          >
            ×
          </button>
        )}
      </div>
      
      {updateProgress && (
        <div style={{ marginTop: '10px' }}>
          <div style={{
            width: '100%',
            height: '6px',
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${updateProgress.percent}%`,
              height: '100%',
              backgroundColor: '#007bff',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      )}
    </div>
  );
}

// Professional Alert Modal Popup
function AlertPopup({ alert, onAcknowledge, userRole }) {
  if (userRole !== 'user' || !alert) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      animation: 'fadeIn 0.3s ease'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '500px',
        width: '90%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        animation: 'slideUp 0.3s ease',
        border: '4px solid #ef4444'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'pulse 1s infinite' }}>🚨</div>
          <h2 style={{ margin: '0', color: '#1f2937', fontSize: '24px', fontWeight: '700' }}>URGENT ALERT</h2>
        </div>
        
        <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#fef2f2', borderRadius: '8px', borderLeft: '4px solid #ef4444' }}>
          <p style={{ margin: '0', color: '#1f2937', fontSize: '16px', lineHeight: '1.6' }}>
            {alert.message}
          </p>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px', fontSize: '13px', color: '#666' }}>
          <div>
            <span style={{ fontWeight: '600', color: '#1f2937' }}>From:</span><br />
            {alert.createdByUsername}
          </div>
          <div>
            <span style={{ fontWeight: '600', color: '#1f2937' }}>Time:</span><br />
            {new Date(alert.createdAt).toLocaleTimeString()}
          </div>
        </div>
        
        <button 
          className="button success" 
          onClick={onAcknowledge}
          style={{
            width: '100%',
            padding: '14px',
            fontSize: '16px',
            fontWeight: '600',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            border: 'none',
            color: 'white',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
        >
          ✅ ACKNOWLEDGE ALERT
        </button>
      </div>
      
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
      `}</style>
    </div>
  );
}

// User Dashboard with integrated inbox
function UserDashboard({ messages = [], loading = false }) {
  const [expanded, setExpanded] = useState(false);
  
  const getTargetDisplay = (msg) => {
    if (msg.targetType === 'specific') return '🎯 Specific User';
    if (msg.targetType === 'NBCU') return '🏢 NBCU Station';
    return '🌐 Telemundo Station';
  };
  
  const getTimeRemaining = (createdAt) => {
    const expiryTime = createdAt + (24 * 60 * 60 * 1000);
    const now = Date.now();
    const remaining = expiryTime - now;
    
    if (remaining <= 0) return 'Expired';
    
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${minutes}m left`;
    return `${minutes}m left`;
  };
  
  const renderMessage = (msg, isPreview = false) => (
    <div key={`${msg.id}`} style={{
      borderLeft: '4px solid #3b82f6',
      padding: '16px',
      marginBottom: '12px',
      borderRadius: '8px',
      background: 'white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      transition: 'all 0.2s'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
        <div>
          <p style={{ margin: '0', fontWeight: '600', color: '#1f2937', fontSize: '15px' }}>
            From: {msg.createdByUsername}
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#999' }}>
            {new Date(msg.createdAt).toLocaleString()}
          </p>
        </div>
        {!isPreview && <span style={{ fontSize: '11px', fontWeight: '600', color: '#10b981' }}>✓</span>}
      </div>
      
      {/* Target and Details */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px', fontSize: '12px' }}>
        <div>
          <span style={{ fontWeight: '600', color: '#6b7280' }}>Target:</span><br />
          <span style={{ color: '#1f2937' }}>{getTargetDisplay(msg)}</span>
        </div>
        <div>
          <span style={{ fontWeight: '600', color: '#6b7280' }}>Expires:</span><br />
          <span style={{ color: msg.createdAt + (24 * 60 * 60 * 1000) - Date.now() < 1 * 60 * 60 * 1000 ? '#ef4444' : '#1f2937' }}>
            {getTimeRemaining(msg.createdAt)}
          </span>
        </div>
      </div>
      
      {/* Message */}
      <div style={{
        padding: '12px',
        background: '#f3f4f6',
        borderRadius: '6px',
        marginBottom: '12px',
        color: '#374151',
        fontSize: '14px',
        lineHeight: '1.6',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight: isPreview ? '100px' : 'none',
        overflow: isPreview ? 'hidden' : 'visible'
      }}>
        {msg.message}
      </div>
    </div>
  );
  
  const displayedMessages = expanded ? messages : messages.slice(0, 2);
  
  return (
    <div>
      {/* Messages Header */}
      <div className="card" style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        color: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '16px',
        boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
        cursor: messages.length > 2 ? 'pointer' : 'default',
        transition: 'all 0.2s'
      }}
      onClick={() => messages.length > 2 && setExpanded(!expanded)}
      onMouseOver={(e) => messages.length > 2 && (e.target.style.opacity = '0.9')}
      onMouseOut={(e) => messages.length > 2 && (e.target.style.opacity = '1')}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600' }}>📬 Inbox</h2>
            <p style={{ margin: '0', opacity: 0.9, fontSize: '13px' }}>
              {messages.length} message{messages.length !== 1 ? 's' : ''} • {expanded ? 'Showing all' : `Showing ${Math.min(2, messages.length)} of ${messages.length}`}
            </p>
          </div>
          {messages.length > 2 && (
            <button style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.4)',
              color: 'white',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}>
              {expanded ? '▼ Collapse' : '▶ View All'}
            </button>
          )}
        </div>
      </div>
      
      {/* Loading State */}
      {loading && (
        <div className="card" style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
          Loading messages...
        </div>
      )}
      
      {/* Empty State */}
      {!loading && messages.length === 0 && (
        <div className="card" style={{
          textAlign: 'center',
          padding: '48px 24px',
          color: '#999',
          borderRadius: '12px',
          background: '#f9fafb'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>✨</div>
          <p style={{ margin: '0', fontSize: '16px', fontWeight: '500' }}>No messages</p>
          <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>You're all caught up!</p>
        </div>
      )}
      
      {/* Messages List */}
      {!loading && messages.length > 0 && (
        <div>
          {displayedMessages.map((msg) => renderMessage(msg))}
        </div>
      )}
    </div>
  );
}

// Admin dashboard with alert creation and inbox integration
function AdminDashboard({ onCreateAlert, userRole, users, onFetchUsers, onUpdateUser, onApproveUser, onRejectUser, onDeleteUser, token, inboxMessages = [], inboxLoading = false, onDeleteMessage, onRefreshInbox }) {
  const [alertMessage, setAlertMessage] = useState('');
  const [targetType, setTargetType] = useState('NBCU');
  const [selectedUserId, setSelectedUserId] = useState('');

  const handleCreateAlert = () => {
    if (!alertMessage.trim()) {
      console.log('⚠️ Please enter a message');
      return;
    }

    if (targetType === 'specific' && !selectedUserId) {
      console.log('⚠️ Please select a user for specific alerts');
      return;
    }

    onCreateAlert(alertMessage, targetType, targetType === 'specific' ? selectedUserId : null);
    setAlertMessage('');
  };

  useEffect(() => {
    console.log('🔍 AdminDashboard useEffect - userRole:', userRole, 'token exists:', !!token);
    if (userRole === 'superadmin' && token) {
      console.log('✅ Calling fetchUsers');
      onFetchUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, token]);

  return (
    <div>
      {/* Alert Creation Card */}
      <div className="card" style={{
        borderTop: '4px solid #ef4444',
        padding: '24px',
        borderRadius: '12px',
        background: 'white',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', color: '#1f2937' }}>📢 Send Alert</h2>
        <textarea
          className="textarea"
          value={alertMessage}
          onChange={(e) => setAlertMessage(e.target.value)}
          placeholder="Enter your alert message... (up to 3000 words, any characters allowed)"
          rows="5"
          style={{
            minHeight: '120px',
            maxHeight: '250px',
            resize: 'vertical',
            width: '100%',
            fontSize: '14px',
            lineHeight: '1.5',
            padding: '12px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
        />
        <div style={{ 
          fontSize: '12px', 
          color: '#999', 
          marginTop: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '16px'
        }}>
          <span>{alertMessage.length} chars</span>
          <span>{alertMessage.trim() ? alertMessage.trim().split(/\s+/).length : 0} words</span>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <select 
            className="select" 
            value={targetType} 
            onChange={(e) => setTargetType(e.target.value)}
            style={{
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          >
            <option value="NBCU">→ NBCU Station</option>
            <option value="Telemundo">→ Telemundo Station</option>
            {userRole === 'superadmin' && (
              <option value="specific">→ Specific User</option>
            )}
          </select>

          {targetType === 'specific' && users && (
            <select 
              className="select" 
              value={selectedUserId} 
              onChange={(e) => setSelectedUserId(e.target.value)}
              style={{
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            >
              <option value="">Select user...</option>
              {[...users.nbcu_users, ...users.telemundo_users].map(user => (
                <option key={user.id} value={user.id}>
                  {user.username} ({user.stationType})
                </option>
              ))}
            </select>
          )}
        </div>

        <button className="button" onClick={handleCreateAlert} style={{
          width: '100%',
          padding: '12px',
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.2s',
          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
        }}>
          📢 Send Alert Now
        </button>
      </div>

      {/* Admin Sent Messages - Same Expandable Layout as User Inbox */}
      {(userRole === 'admin' || userRole === 'superadmin') && (
        <AdminSentMessages 
          messages={inboxMessages}
          loading={inboxLoading}
          onRefresh={onRefreshInbox}
          onDelete={onDeleteMessage}
          token={token}
        />
      )}

      {userRole === 'superadmin' && (
        <SuperAdminPanel 
          users={users} 
          onUpdateUser={onUpdateUser}
          onRefresh={onFetchUsers}
          onApproveUser={onApproveUser}
          onRejectUser={onRejectUser}
          onDeleteUser={onDeleteUser}
          token={token}
        />
      )}
    </div>
  );
}

// Super admin user management panel
function SuperAdminPanel({ users, onUpdateUser, onRefresh, onApproveUser, onRejectUser, onDeleteUser, token }) {
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [loadingDomains, setLoadingDomains] = useState(false);

  const fetchDomains = async () => {
    setLoadingDomains(true);
    try {
      const response = await fetch(`${API_BASE_URL}/domains`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      const data = await response.json();
      if (response.ok) {
        setDomains(data.domains || []);
      } else {
        console.error('❌ Failed to fetch domains:', data.error);
      }
    } catch (error) {
      console.error('❌ Error fetching domains:', error);
    } finally {
      setLoadingDomains(false);
    }
  };

  const addDomain = async () => {
    if (!newDomain.trim()) {
      console.log('⚠️ Please enter a domain');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/domains`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ domain: newDomain.trim() }),
      });

      const data = await response.json();
      if (response.ok) {
        console.log('✅ Domain added successfully');
        setNewDomain('');
        fetchDomains();
      } else {
        console.error('❌ Failed to add domain:', data.error);
      }
    } catch (error) {
      console.error('❌ Error adding domain:', error);
    }
  };

  const removeDomain = async (domain) => {
    // Direct deletion - no confirmation
    try {
      const response = await fetch(`${API_BASE_URL}/domains/${encodeURIComponent(domain)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = await response.json();
      if (response.ok) {
        console.log('✅ Domain removed successfully');
        fetchDomains();
      } else {
        console.error('❌ Failed to remove domain:', data.error);
      }
    } catch (error) {
      console.error('❌ Error removing domain:', error);
    }
  };

  useEffect(() => {
    fetchDomains();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!users) {
    return (
      <div className="card">
        <h2>User Management</h2>
        <button className="button" onClick={onRefresh}>Load Users</button>
      </div>
    );
  }

  const renderUserList = (userList, title) => (
    <div>
      <h4>{title} ({userList.length})</h4>
      <div style={{ 
      display: 'grid', 
        gridTemplateColumns: '3fr 1.5fr 1.5fr 1fr', 
        gap: '10px', 
        padding: '10px', 
        backgroundColor: '#f5f5f5', 
        fontWeight: 'bold', 
        borderRadius: '4px', 
        marginBottom: '5px' 
      }}>
        <div>User</div>
        <div>Role</div>
        <div>Station Type</div>
        <div>Actions</div>
      </div>
      {userList.map(user => (
        <div key={user.id} style={{ 
          display: 'grid', 
          gridTemplateColumns: '3fr 1.5fr 1.5fr 1fr', 
          gap: '10px', 
          alignItems: 'center',
          border: '1px solid #ddd', 
          padding: '10px', 
          margin: '5px 0',
          borderRadius: '4px' 
        }}>
          <div>
            <strong>{user.username}</strong><br />
            <small style={{ color: '#666' }}>({user.email})</small>
            {/* User Status - Based on live connections */}
            <div style={{ marginTop: '4px', fontSize: '11px', fontWeight: '500' }}>
              {user.isConnected ? (
                <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981', display: 'inline-block' }}></span>
                  🟢 Online
                </span>
              ) : (
                <span style={{ color: '#d1d5db', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#d1d5db', display: 'inline-block' }}></span>
                  ⚫ Offline
                </span>
              )}
            </div>
          </div>
          
          <div>
            <select 
              defaultValue={user.role}
              onChange={(e) => onUpdateUser(user.id, e.target.value, user.stationType)}
              style={{ width: '100%', fontSize: '12px' }}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="superadmin">Super Admin</option>
            </select>
          </div>
          
          <div>
            <select 
              defaultValue={user.stationType}
              onChange={(e) => onUpdateUser(user.id, user.role, e.target.value)}
              style={{ width: '100%', fontSize: '12px' }}
            >
              <option value="NBCU">NBCU</option>
              <option value="Telemundo">Telemundo</option>
            </select>
          </div>
          
          <div>
            <button 
              className="button danger" 
              onClick={() => onDeleteUser(user.id)}
              style={{ fontSize: '11px', padding: '5px 10px', width: '100%' }}
            >
              🗑️ Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderPendingUserList = (userList, title) => {
    if (!userList || userList.length === 0) return null;
    
    return (
      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fff3cd', border: '2px solid #ffc107', borderRadius: '8px' }}>
        <h4 style={{ color: '#856404', marginTop: 0 }}>⏳ {title} ({userList.length})</h4>
        <p style={{ fontSize: '13px', color: '#856404', marginBottom: '15px' }}>These users require approval before they can log in.</p>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '2fr 2fr 1fr 2fr', 
          gap: '10px', 
          padding: '10px', 
          backgroundColor: 'rgba(255, 255, 255, 0.7)', 
          fontWeight: 'bold', 
          borderRadius: '4px', 
          marginBottom: '5px' 
        }}>
          <div>Username</div>
          <div>Email</div>
          <div>Station Type</div>
          <div>Actions</div>
        </div>
        {userList.map(user => (
          <div key={user.id} style={{ 
            display: 'grid', 
            gridTemplateColumns: '2fr 2fr 1fr 2fr', 
            gap: '10px', 
            alignItems: 'center',
            border: '1px solid #ddd', 
            padding: '10px', 
            margin: '5px 0',
            borderRadius: '4px',
            backgroundColor: 'white'
          }}>
            <div><strong>{user.username}</strong></div>
            <div style={{ fontSize: '13px' }}>{user.email}</div>
            <div style={{ fontSize: '13px' }}>{user.stationType}</div>
            <div style={{ display: 'flex', gap: '5px' }}>
              <button 
                className="button" 
                onClick={() => onApproveUser(user.id)}
                style={{ fontSize: '11px', padding: '5px 10px', backgroundColor: '#28a745', flex: 1 }}
              >
                ✅ Approve
              </button>
              <button 
                className="button danger" 
                onClick={() => onRejectUser(user.id)}
                style={{ fontSize: '11px', padding: '5px 10px', flex: 1 }}
              >
                ❌ Reject
              </button>
              <button 
                className="button danger" 
                onClick={() => onDeleteUser(user.id)}
                style={{ fontSize: '11px', padding: '5px 10px', backgroundColor: '#dc3545' }}
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderRejectedUserList = (userList, title) => {
    if (!userList || userList.length === 0) return null;
    
    return (
      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8d7da', border: '2px solid #dc3545', borderRadius: '8px' }}>
        <h4 style={{ color: '#721c24', marginTop: 0 }}>🚫 {title} ({userList.length})</h4>
        <p style={{ fontSize: '13px', color: '#721c24', marginBottom: '15px' }}>These users have been rejected and cannot log in.</p>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '2fr 2fr 1fr 2fr', 
          gap: '10px', 
          padding: '10px', 
          backgroundColor: 'rgba(255, 255, 255, 0.7)', 
          fontWeight: 'bold', 
          borderRadius: '4px', 
          marginBottom: '5px' 
        }}>
          <div>Username</div>
          <div>Email</div>
          <div>Station Type</div>
          <div>Actions</div>
        </div>
        {userList.map(user => (
          <div key={user.id} style={{ 
            display: 'grid', 
            gridTemplateColumns: '2fr 2fr 1fr 2fr', 
            gap: '10px', 
            alignItems: 'center',
            border: '1px solid #ddd', 
            padding: '10px', 
            margin: '5px 0',
            borderRadius: '4px',
            backgroundColor: 'white'
          }}>
            <div><strong>{user.username}</strong></div>
            <div style={{ fontSize: '13px' }}>{user.email}</div>
            <div style={{ fontSize: '13px' }}>{user.stationType}</div>
            <div style={{ display: 'flex', gap: '5px' }}>
              <button 
                className="button" 
                onClick={() => onApproveUser(user.id)}
                style={{ fontSize: '11px', padding: '5px 10px', backgroundColor: '#28a745', flex: 1 }}
              >
                ✅ Approve
              </button>
              <button 
                className="button danger" 
                onClick={() => onDeleteUser(user.id)}
                style={{ fontSize: '11px', padding: '5px 10px', backgroundColor: '#dc3545' }}
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };


  return (
    <div>
      <div className="card">
        <h2>User Management</h2>
        <button className="button" onClick={onRefresh}>Refresh Users</button>
        <p>Total Approved Users: {users.nbcu_users.length + users.telemundo_users.length + users.admins.length + users.superadmins.length}</p>
        
        {/* Pending users - show first for visibility */}
        {renderPendingUserList(users.pending_users, 'Pending Users Awaiting Approval')}
        
        {/* Rejected users */}
        {renderRejectedUserList(users.rejected_users, 'Rejected Users')}
        
        {/* Approved users */}
        {renderUserList(users.superadmins, 'Super Admins')}
        {renderUserList(users.admins, 'Admins')}
        {renderUserList(users.nbcu_users, 'NBCU Users')}
        {renderUserList(users.telemundo_users, 'Telemundo Users')}
      </div>

      {/* Domain Whitelist Management */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>🔐 Email Domain Whitelist</h2>
          <button className="button" onClick={fetchDomains} disabled={loadingDomains}>
            {loadingDomains ? 'Loading...' : '🔄 Refresh'}
          </button>
        </div>
        
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Only users with email addresses from these domains can register. 
          Users from other domains will be blocked during registration.
        </p>
        
        {/* Add Domain Form */}
        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px'
        }}>
          <input
            type="text"
            className="input"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder="Enter domain (e.g., nbcuni.com or @nbcuni.com)"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                addDomain();
              }
            }}
            style={{ flex: 1 }}
          />
          <button className="button" onClick={addDomain}>
            ➕ Add Domain
          </button>
        </div>
        
        {/* Domains List */}
        {loadingDomains ? (
          <p style={{ textAlign: 'center', color: '#666' }}>Loading domains...</p>
        ) : domains.length === 0 ? (
          <div style={{ 
            padding: '30px', 
            textAlign: 'center', 
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px'
          }}>
            <p style={{ margin: 0, color: '#856404' }}>
              ⚠️ No domains whitelisted. Registration is currently blocked for all users.
            </p>
          </div>
        ) : (
          <div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '2fr 1fr 1fr 100px', 
              gap: '10px', 
              padding: '10px', 
              backgroundColor: '#f5f5f5', 
              fontWeight: 'bold', 
              borderRadius: '4px', 
              marginBottom: '10px' 
            }}>
              <div>Domain</div>
              <div>Added By</div>
              <div>Added Date</div>
              <div>Actions</div>
            </div>
            
            {domains.map((domain) => (
              <div 
                key={domain.domain}
                style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '2fr 1fr 1fr 100px', 
                  gap: '10px', 
                  alignItems: 'center',
                  border: '1px solid #ddd', 
                  padding: '15px', 
                  margin: '5px 0',
                  borderRadius: '4px',
                  backgroundColor: '#fff'
                }}
              >
                <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                  @{domain.domain}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {domain.addedBy || 'Unknown'}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {domain.addedAt ? new Date(domain.addedAt).toLocaleDateString() : 'Unknown'}
                </div>
                <div>
                  <button 
                    className="button danger small"
                    onClick={() => removeDomain(domain.domain)}
                    style={{ width: '100%', fontSize: '12px' }}
                  >
                    🗑️ Remove
                  </button>
                </div>
              </div>
            ))}
            
            <div style={{ 
              marginTop: '15px', 
              padding: '10px', 
              backgroundColor: '#e7f3ff',
              border: '1px solid #b3d9ff',
              borderRadius: '4px'
            }}>
              <p style={{ margin: 0, fontSize: '12px', color: '#004085' }}>
                💡 <strong>Tip:</strong> Add domains like "nbcuni.com" (without @). 
                Users will only be able to register if their email matches one of these domains.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Login and registration page
function LoginPage({ onLogin, onRegister, isLoading, handleSSOLogin }) {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: string }
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    stationType: 'NBCU'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null); // Clear previous messages
    
    if (isLoginMode) {
      const result = await onLogin(formData.email, formData.password);
      if (!result.success) {
        setMessage({ 
          type: 'error', 
          text: result.error || 'Login failed'
        });
      }
      // If successful, the user state will update and LoginPage will unmount
    } else {
      const result = await onRegister(formData.username, formData.email, formData.password, formData.stationType);
      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: result.message || 'Registration successful! Your account is pending approval by an administrator. You will be able to log in once approved.'
        });
        // Clear form on success
        setFormData({ username: '', email: '', password: '', stationType: 'NBCU' });
      } else {
        setMessage({ 
          type: 'error', 
          text: result.error || 'Registration failed'
        });
      }
    }
  };

  // Get version info
  const packageVersion = process.env.REACT_APP_VERSION || '1.0.0';
  const buildTime = process.env.REACT_APP_BUILD_TIME || 'Unknown';
  const isDesktopApp = window.electronAPI ? true : false;

  return (
    <div className="container">
      <div className="card" style={{ maxWidth: '400px', margin: '50px auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <h1 style={{ color: '#1f2937', fontSize: '32px', marginBottom: '12px' }}>SOS Alert System</h1>
        <h2 style={{ color: '#6b7280', fontSize: '18px', fontWeight: '500', marginBottom: '24px' }}>{isLoginMode ? 'Sign In' : 'Create Account'}</h2>
        
        {/* Version Information */}
        <div style={{ 
          textAlign: 'center', 
          fontSize: '11px', 
          color: '#666', 
          marginBottom: '15px',
          padding: '8px',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px'
        }}>
          <div>📱 {isDesktopApp ? 'Desktop App' : 'Web App'} v{packageVersion}</div>
          <div>🕐 Built: {new Date(buildTime).toLocaleString()}</div>
          {isDesktopApp && <div>🔄 Auto-updates enabled</div>}
        </div>
        
        {/* Success/Error Messages */}
        {message && (
          <div style={{ 
            padding: '12px 16px', 
            marginBottom: '16px',
            borderRadius: '6px',
            backgroundColor: message.type === 'success' ? '#ecfdf5' : '#fef2f2',
            border: `1px solid ${message.type === 'success' ? '#d1fae5' : '#fee2e2'}`,
            color: message.type === 'success' ? '#047857' : '#991b1b',
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            {message.type === 'success' ? '✅' : '❌'} {message.text}
          </div>
        )}
        
        {/* SSO Login Buttons (only show in login mode) */}
        {isLoginMode && (SSO_CONFIG.samlEnabled || SSO_CONFIG.oidcEnabled) && (
          <div style={{ marginBottom: '24px' }}>
            {SSO_CONFIG.samlEnabled && (
              <button
                type="button"
                onClick={() => handleSSOLogin(SSO_CONFIG.samlProviderName)}
                className="button"
                style={{
                  width: '100%',
                  backgroundColor: '#0078d4',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                disabled={isLoading}
              >
                <span>🔐</span>
                <span>Sign in with {SSO_CONFIG.samlProviderName}</span>
              </button>
            )}
            
            {SSO_CONFIG.oidcEnabled && (
              <button
                type="button"
                onClick={() => handleSSOLogin(SSO_CONFIG.oidcProviderName)}
                className="button"
                style={{
                  width: '100%',
                  backgroundColor: '#008272',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                disabled={isLoading}
              >
                <span>🔑</span>
                <span>Sign in with {SSO_CONFIG.oidcProviderName}</span>
              </button>
            )}
            
            <div style={{
              textAlign: 'center',
              margin: '16px 0',
              color: '#6b7280',
              fontSize: '14px',
              position: 'relative'
            }}>
              <span style={{
                backgroundColor: 'white',
                padding: '0 12px',
                position: 'relative',
                zIndex: 1
              }}>or use email</span>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: '1px',
                backgroundColor: '#e5e7eb',
                zIndex: 0
              }}></div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          {!isLoginMode && (
            <input
              className="input"
              type="text"
              placeholder="Username"
              value={formData.username}
              onChange={(e) => setFormData({...formData, username: e.target.value})}
              required
            />
          )}
          
          <input
            className="input"
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
          />
          
          <input
            className="input"
            type="password"
            placeholder="Password"
            value={formData.password}
            onChange={(e) => setFormData({...formData, password: e.target.value})}
            required
          />
          
          {!isLoginMode && (
            <select
              className="select"
              value={formData.stationType}
              onChange={(e) => setFormData({...formData, stationType: e.target.value})}
            >
              <option value="NBCU">NBCU Station</option>
              <option value="Telemundo">Telemundo Station</option>
            </select>
          )}
          
          <button 
            className="button" 
            type="submit" 
            disabled={isLoading}
            style={{ width: '100%' }}
          >
            {isLoading ? 'Loading...' : (isLoginMode ? 'Login' : 'Register')}
          </button>
        </form>
        
        <p style={{ textAlign: 'center', marginTop: '20px' }}>
          {isLoginMode ? "Don't have an account?" : "Already have an account?"}
          <button 
            className="button" 
            onClick={() => setIsLoginMode(!isLoginMode)}
            style={{ marginLeft: '10px' }}
          >
            {isLoginMode ? 'Register' : 'Login'}
          </button>
        </p>
      </div>
    </div>
  );
}

// Inbox Dashboard for viewing message history
function InboxDashboard({ messages, onRefresh, onDeleteMessage, userRole }) {
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatTimeLeft = (timestamp) => {
    if (!timestamp) return 'No expiry';
    
    const expiryTime = timestamp * 1000; // Convert to milliseconds
    const now = Date.now();
    const timeLeft = expiryTime - now;
    
    if (timeLeft <= 0) return 'Expired';
    
    const hours = Math.floor(timeLeft / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m left`;
    } else {
      return `${minutes}m left`;
    }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>📥 Message Inbox</h2>
        <button className="button" onClick={onRefresh}>
          🔄 Refresh
        </button>
      </div>
      
      <p>Messages are automatically deleted 24 hours after acknowledgment.</p>
      
      {messages.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
          No messages in your inbox
        </p>
      ) : (
        <div className="message-list">
          {messages.map(message => (
            <div key={`${message.id}-${message.timestamp}`} className="message-item">
              <div className="message-header">
                <strong>From: {message.createdByUsername}</strong>
                <span className="message-date">
                  {formatDate(message.acknowledgedAt || message.createdAt)}
                </span>
              </div>
              
              <div className="message-content">
                <p>{message.message}</p>
              </div>
              
              <div className="message-footer">
                <div className="message-info">
                  <span>Target: {message.targetType === 'specific' ? 'Individual' : message.targetType}</span>
                  {message.expiresAt && (
                    <span className="expiry-info">
                      ⏰ {formatTimeLeft(message.expiresAt)}
                    </span>
                  )}
                </div>
                
                {userRole === 'superadmin' && (
                  <button 
                    className="button danger small"
                    onClick={() => onDeleteMessage(message.id)}
                  >
                    🗑️ Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Admin Sent Messages Component - Professional expandable layout matching user inbox
function AdminSentMessages({ messages = [], loading = false, onRefresh, onDelete, token }) {
  const [expanded, setExpanded] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const [userMap, setUserMap] = useState({});
  
  // Fetch users to map IDs to names/emails
  useEffect(() => {
    if (!token) {
      console.log('⚠️ AdminSentMessages: Skipping user fetch - no token');
      return;
    }
    
    const fetchUsersForModal = async () => {
      try {
        console.log('📋 AdminSentMessages: Fetching users for modal');
        const response = await fetch(`${API_BASE_URL}/users`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (response.ok) {
          const data = await response.json();
          const users = data.users;
          
          // Create map: userId -> {username, email}
          const map = {};
          [users.superadmins, users.admins, users.nbcu_users, users.telemundo_users, users.pending_users, users.rejected_users]
            .flat()
            .forEach(user => {
              map[user.id] = { username: user.username, email: user.email };
            });
          
          setUserMap(map);
          console.log('User map created with', Object.keys(map).length, 'users');
        }
      } catch (error) {
        console.error('Error fetching users for modal:', error);
      }
    };
    
    fetchUsersForModal();
  }, [token]);
  
  const getTargetDisplay = (msg) => {
    if (msg.targetType === 'specific') return '🎯 Specific User';
    if (msg.targetType === 'NBCU') return '🏢 NBCU Station';
    return '🌐 Telemundo Station';
  };
  
  const getTimeRemaining = (createdAt) => {
    const expiryTime = createdAt + (24 * 60 * 60 * 1000);
    const now = Date.now();
    const remaining = expiryTime - now;
    
    if (remaining <= 0) return 'Expired';
    
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${minutes}m left`;
    return `${minutes}m left`;
  };
  
  const getDeliveryStats = (msg) => {
    const stats = { pending: 0, delivered: 0, seen: 0 };
    if (!msg.deliveryStatus) return stats;
    
    Object.values(msg.deliveryStatus).forEach(status => {
      if (status === 'seen') stats.seen++;
      else if (status === 'delivered') stats.delivered++;
      else stats.pending++;
    });
    return stats;
  };
  
  const renderDeliveryStatus = (msg) => {
    const stats = getDeliveryStats(msg);
    return (
      <div style={{
        display: 'flex',
        gap: '16px',
        padding: '8px 12px',
        background: '#f3f4f6',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        fontSize: '12px'
      }}
      onClick={() => setDetailModal(msg)}
      onMouseOver={(e) => e.currentTarget.style.background = '#e5e7eb'}
      onMouseOut={(e) => e.currentTarget.style.background = '#f3f4f6'}
      >
        {stats.seen > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#3b82f6' }}>
            <span style={{ fontWeight: 'bold' }}>✓✓</span>
            <span>{stats.seen} Seen</span>
          </div>
        )}
        {stats.delivered > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280' }}>
            <span style={{ fontWeight: 'bold' }}>✓✓</span>
            <span>{stats.delivered} Delivered</span>
          </div>
        )}
        {stats.pending > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#d1d5db' }}>
            <span style={{ fontWeight: 'bold' }}>✓</span>
            <span>{stats.pending} Pending</span>
          </div>
        )}
      </div>
    );
  };
  
  const renderMessage = (msg) => (
    <div key={msg.id} style={{
      borderLeft: '4px solid #3b82f6',
      padding: '16px',
      marginBottom: '12px',
      borderRadius: '8px',
      background: 'white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      transition: 'all 0.2s'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
        <div>
          <p style={{ margin: '0', fontWeight: '600', color: '#1f2937', fontSize: '15px' }}>
            {msg.message.substring(0, 60)}{msg.message.length > 60 ? '...' : ''}
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#999' }}>
            {new Date(msg.createdAt).toLocaleString()}
          </p>
        </div>
        {onDelete && (
          <button onClick={() => onDelete(msg.id)} style={{
            background: 'none',
            border: 'none',
            color: '#999',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '4px 8px',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.color = '#ef4444'}
          onMouseOut={(e) => e.target.style.color = '#999'}
          >
            🗑️
          </button>
        )}
      </div>
      
      {/* Target and Details */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px', fontSize: '12px' }}>
        <div>
          <span style={{ fontWeight: '600', color: '#6b7280' }}>To:</span><br />
          <span style={{ color: '#1f2937' }}>{getTargetDisplay(msg)}</span>
        </div>
        <div>
          <span style={{ fontWeight: '600', color: '#6b7280' }}>Expires:</span><br />
          <span style={{ color: msg.createdAt + (24 * 60 * 60 * 1000) - Date.now() < 1 * 60 * 60 * 1000 ? '#ef4444' : '#1f2937' }}>
            {getTimeRemaining(msg.createdAt)}
          </span>
        </div>
      </div>
      
      {/* Delivery Status - Clickable */}
      {renderDeliveryStatus(msg)}
    </div>
  );
  
  const displayedMessages = expanded ? messages : messages.slice(0, 2);
  
  return (
    <div>
      {/* Header */}
      <div className="card" style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        color: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '16px',
        boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
        cursor: messages.length > 2 ? 'pointer' : 'default',
        transition: 'all 0.2s',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
      onClick={() => messages.length > 2 && setExpanded(!expanded)}
      onMouseOver={(e) => messages.length > 2 && (e.currentTarget.style.opacity = '0.9')}
      onMouseOut={(e) => messages.length > 2 && (e.currentTarget.style.opacity = '1')}
      >
        <div>
          <h2 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600' }}>📤 Sent Messages</h2>
          <p style={{ margin: '0', opacity: 0.9, fontSize: '13px' }}>
            {messages.length} message{messages.length !== 1 ? 's' : ''} • {expanded ? 'Showing all' : `Showing ${Math.min(2, messages.length)} of ${messages.length}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button onClick={(e) => { e.stopPropagation(); onRefresh(); }} style={{
            background: 'rgba(255,255,255,0.2)',
            border: '1px solid rgba(255,255,255,0.4)',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
          onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
          >
            🔄 Refresh
          </button>
          {messages.length > 2 && (
            <button style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.4)',
              color: 'white',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}>
              {expanded ? '▼ Collapse' : '▶ View All'}
            </button>
          )}
        </div>
      </div>
      
      {/* Loading State */}
      {loading && (
        <div className="card" style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
          Loading messages...
        </div>
      )}
      
      {/* Empty State */}
      {!loading && messages.length === 0 && (
        <div className="card" style={{
          textAlign: 'center',
          padding: '48px 24px',
          color: '#999',
          borderRadius: '12px',
          background: '#f9fafb'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>📭</div>
          <p style={{ margin: '0', fontSize: '16px', fontWeight: '500' }}>No messages sent yet</p>
        </div>
      )}
      
      {/* Messages List */}
      {!loading && messages.length > 0 && (
        <div>
          {displayedMessages.map((msg) => renderMessage(msg))}
        </div>
      )}
      
      {/* Detail Modal */}
      {detailModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}
        onClick={() => setDetailModal(null)}
        >
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: '0', fontSize: '18px', color: '#1f2937' }}>Delivery Status</h2>
              <button onClick={() => setDetailModal(null)} style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#999'
              }}>×</button>
            </div>
            
            <div style={{ marginBottom: '20px', padding: '12px', background: '#f3f4f6', borderRadius: '6px' }}>
              <p style={{ margin: '0', fontSize: '13px', color: '#1f2937', fontWeight: '500' }}>
                {detailModal.message.substring(0, 100)}{detailModal.message.length > 100 ? '...' : ''}
              </p>
            </div>
            
            {detailModal.deliveryStatus && Object.keys(detailModal.deliveryStatus).length > 0 ? (
              <div>
                {['seen', 'delivered', 'pending'].map((status) => {
                  const users = Object.entries(detailModal.deliveryStatus)
                    .filter(([_, s]) => (status === 'pending' ? s !== 'seen' && s !== 'delivered' : s === status))
                    .map(([uid, _]) => uid);
                  
                  if (users.length === 0) return null;
                  
                  let icon = '✓';
                  let label = 'Pending';
                  let color = '#d1d5db';
                  
                  if (status === 'seen') {
                    icon = '✓✓';
                    label = 'Seen';
                    color = '#3b82f6';
                  } else if (status === 'delivered') {
                    icon = '✓✓';
                    label = 'Delivered';
                    color = '#6b7280';
                  }
                  
                  return (
                    <div key={status} style={{ marginBottom: '12px' }}>
                      <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                        <span style={{ color: color }}>{icon}</span> {label}
                      </p>
                      {users.map(uid => {
                        const userData = userMap[uid];
                        return (
                          <div key={uid} style={{ padding: '8px 12px', background: '#f9fafb', borderRadius: '4px', marginBottom: '4px', fontSize: '13px', color: '#1f2937' }}>
                            <div style={{ fontWeight: '500' }}>{userData?.username || 'Unknown User'}</div>
                            <div style={{ fontSize: '12px', color: '#666' }}>{userData?.email || uid}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: '#999', fontSize: '13px' }}>No delivery status available</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
